import { Copy, ExternalLink, AlertCircle, CheckCircle, Zap } from 'lucide-react';
import { useState } from 'react';

/**
 * Affiché quand VITE_CLERK_PUBLISHABLE_KEY est absente.
 * Guide Sebastian pour configurer Clerk en 5 minutes,
 * OU activer le mode démo en 30 secondes.
 */
export function SetupRequiredPage({ missingVars }: { missingVars: string[] }) {
  return (
    <div className="min-h-screen bg-ducens-bg flex items-center justify-center p-6">
      <div className="max-w-2xl w-full bg-white border border-ducens-border rounded-xl p-8 shadow-sm">
        <div className="flex items-start gap-3 mb-6">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-ducens-warn/10 flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-ducens-warn" />
          </div>
          <div>
            <h1 className="text-xl font-semibold leading-tight">Configuration requise</h1>
            <p className="text-sm text-ducens-muted mt-1">
              L'app marche mais il manque {missingVars.length} variable
              {missingVars.length > 1 ? 's' : ''} dans <code className="px-1 py-0.5 bg-ducens-bg rounded">.env.local</code>.
            </p>
          </div>
        </div>

        {/* Raccourci mode démo */}
        <div className="mb-6 bg-ducens-accent/30 border border-ducens-accent rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-ducens-ink shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-sm">Voie rapide : mode démo (30 secondes)</div>
              <p className="text-sm text-ducens-muted mt-1 mb-2">
                Voir le dashboard tout de suite avec des données fictives. Aucun compte à créer.
              </p>
              <ol className="text-sm space-y-1 text-ducens-ink mb-3">
                <li>
                  1. Crée un fichier <code className="px-1 py-0.5 bg-white rounded text-xs">.env.local</code> à la
                  racine du dossier
                </li>
                <li>2. Mets dedans uniquement la ligne ci-dessous :</li>
              </ol>
              <CopyableCode code="VITE_DEMO_MODE=true" />
              <ol className="text-sm space-y-1 text-ducens-ink mt-3" start={3}>
                <li>3. Redémarre le serveur (Ctrl+C dans le terminal puis <code className="px-1 py-0.5 bg-white rounded text-xs">npm run dev</code>)</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 my-6">
          <div className="flex-1 h-px bg-ducens-border" />
          <span className="text-xs uppercase tracking-wide text-ducens-muted">ou</span>
          <div className="flex-1 h-px bg-ducens-border" />
        </div>

        <h2 className="font-semibold mb-4">Voie complète : avec Clerk + Supabase (5 min)</h2>

        <ol className="space-y-5">
          <Step
            n={1}
            title="Copie .env.example vers .env.local"
            command="copy .env.example .env.local"
            cwd="dans le dossier app-ducens"
          />

          <Step
            n={2}
            title="Crée un compte Clerk (gratuit)"
            link="https://dashboard.clerk.com"
            description='Clique "Add application" → nom "Ducens Dashboard" → onglet "API Keys" → copie la "Publishable key" (commence par pk_test_).'
          />

          <Step
            n={3}
            title="Colle la clé dans .env.local"
            description="Ouvre .env.local et remplace la valeur de VITE_CLERK_PUBLISHABLE_KEY par celle copiée à l'étape 2."
            code={`VITE_CLERK_PUBLISHABLE_KEY=pk_test_xxxxx`}
          />

          <Step
            n={4}
            title="Active le mode démo (optionnel)"
            description="Pour voir le dashboard sans backend démarré, ajoute aussi :"
            code={`VITE_USE_MOCKS=true`}
          />

          <Step
            n={5}
            title="Redémarre le serveur"
            command="npm run dev"
            cwd="depuis le même dossier"
          />
        </ol>

        <div className="mt-6 pt-5 border-t border-ducens-border">
          <h2 className="text-sm font-semibold mb-2">Variables manquantes</h2>
          <div className="space-y-1.5">
            {missingVars.map((v) => (
              <div key={v} className="flex items-center gap-2 text-sm">
                <AlertCircle className="w-3.5 h-3.5 text-ducens-warn shrink-0" />
                <code className="px-1.5 py-0.5 bg-ducens-bg rounded text-xs">{v}</code>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CopyableCode({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-stretch gap-2">
      <pre className="flex-1 px-2.5 py-1.5 bg-white border border-ducens-accent rounded-md text-xs font-mono overflow-x-auto">
        {code}
      </pre>
      <button
        type="button"
        onClick={() => {
          navigator.clipboard.writeText(code);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="shrink-0 px-2.5 py-1.5 bg-white border border-ducens-accent rounded-md text-xs hover:bg-ducens-accent/30 flex items-center gap-1"
      >
        {copied ? <CheckCircle className="w-3 h-3 text-ducens-success" /> : <Copy className="w-3 h-3" />}
        {copied ? 'OK' : 'Copier'}
      </button>
    </div>
  );
}

function Step({
  n,
  title,
  description,
  command,
  code,
  cwd,
  link,
}: {
  n: number;
  title: string;
  description?: string;
  command?: string;
  code?: string;
  cwd?: string;
  link?: string;
}) {
  const [copied, setCopied] = useState(false);

  const copyableText = command ?? code;

  return (
    <li className="flex gap-3">
      <div className="shrink-0 w-6 h-6 rounded-full bg-ducens-ink text-white text-xs font-semibold flex items-center justify-center mt-0.5">
        {n}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">{title}</div>
        {description && <div className="text-sm text-ducens-muted mt-1">{description}</div>}
        {cwd && <div className="text-xs text-ducens-muted mt-1 italic">{cwd}</div>}
        {copyableText && (
          <div className="mt-2 flex items-stretch gap-2">
            <pre className="flex-1 px-2.5 py-1.5 bg-ducens-bg border border-ducens-border rounded-md text-xs font-mono overflow-x-auto">
              {copyableText}
            </pre>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(copyableText);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
              className="shrink-0 px-2.5 py-1.5 border border-ducens-border rounded-md text-xs hover:bg-ducens-bg flex items-center gap-1"
            >
              {copied ? <CheckCircle className="w-3 h-3 text-ducens-success" /> : <Copy className="w-3 h-3" />}
              {copied ? 'OK' : 'Copier'}
            </button>
          </div>
        )}
        {link && (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-sm text-ducens-ink underline underline-offset-2 hover:opacity-70"
          >
            {link} <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </li>
  );
}
